import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const HTMLHeadings = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Text Content & Typography</div>
                <h1>Headings Hierarchy (H1-H6)</h1>
                <p className="hero-lead">
                    Headings are the backbone of document structure. Master the hierarchy from H1 to H6
                    for better SEO, accessibility, and content organization.
                </p>
            </div>

            <div className="content-section">
                <h2>The Six Heading Levels</h2>
                <p>
                    HTML provides six levels of headings, from <code>&lt;h1&gt;</code> (most important) to
                    <code>&lt;h6&gt;</code> (least important). They create a hierarchical outline of your content.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>All Heading Levels</span>
                    </div>
                    <CodeBlock language="html" code={`<h1>Main Page Title (H1)</h1>
<h2>Section Heading (H2)</h2>
<h3>Subsection Heading (H3)</h3>
<h4>Sub-subsection Heading (H4)</h4>
<h5>Minor Heading (H5)</h5>
<h6>Smallest Heading (H6)</h6>`} />
                </div>

                <div className="visual-example">
                    <h3>Visual Hierarchy:</h3>
                    <div className="heading-showcase">
                        <h1 style={{ fontSize: '2.5em', marginBottom: '0.5em' }}>H1 - Main Title</h1>
                        <h2 style={{ fontSize: '2em', marginBottom: '0.5em' }}>H2 - Section Title</h2>
                        <h3 style={{ fontSize: '1.75em', marginBottom: '0.5em' }}>H3 - Subsection</h3>
                        <h4 style={{ fontSize: '1.5em', marginBottom: '0.5em' }}>H4 - Sub-subsection</h4>
                        <h5 style={{ fontSize: '1.25em', marginBottom: '0.5em' }}>H5 - Minor Heading</h5>
                        <h6 style={{ fontSize: '1em', marginBottom: '0.5em' }}>H6 - Smallest Heading</h6>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>The H1 Element: Your Page Title</h2>
                <p>
                    The <code>&lt;h1&gt;</code> is the most important headingit represents the main topic of your page.
                </p>

                <div className="rule-box important">
                    <h3>H1 Best Practices:</h3>
                    <ul>
                        <li>Use <strong>one H1 per page</strong> (most important rule)</li>
                        <li>Make it descriptive and relevant to page content</li>
                        <li>Include your primary keyword if doing SEO</li>
                        <li>Keep it concise (ideally under 70 characters)</li>
                        <li> Don't use multiple H1s (confuses search engines)</li>
                        <li> Don't make it too generic ("Welcome" or "Home")</li>
                    </ul>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span className="correct">Good H1 Examples</span>
                    </div>
                    <CodeBlock language="html" code={`<h1>Complete Guide to HTML Headings</h1>
<h1>How to Master JavaScript in 30 Days</h1>
<h1>The Ultimate SEO Checklist for 2025</h1>`} />
                </div>

                <div className="code-example error">
                    <div className="code-header">
                        <span className="incorrect"> Bad H1 Examples</span>
                    </div>
                    <CodeBlock language="html" code={`<h1>Welcome</h1>              <!-- Too generic --&gt;
<h1>Page 1</h1>               <!-- Not descriptive --&gt;
<h1>Click Here</h1>           <!-- Not informative --&gt;
<h1>Learn About Everything About Web Development, HTML, CSS, JavaScript, and More</h1>  <!-- Too long --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Heading Hierarchy Rules</h2>
                <p>
                    Headings should form a logical outline. Think of them like a table of contents in a book.
                </p>

                <div className="rule-box">
                    <h3>Key Rules:</h3>
                    <ol>
                        <li><strong>Don't skip levels:</strong> Go from H2 to H3, not H2 to H4</li>
                        <li><strong>Maintain logical flow:</strong> Each heading level indicates content hierarchy</li>
                        <li><strong>Use headings for structure, not styling:</strong> Don't choose headings based on size</li>
                    </ol>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span className="correct">Correct Hierarchy</span>
                    </div>
                    <CodeBlock language="html" code={`<h1>Complete Web Development Course</h1>

<h2>1. Introduction to HTML</h2>
<h3>What is HTML?</h3>
<h3>HTML History</h3>
<h4>HTML 1.0</h4>
<h4>HTML 2.0</h4>
<h4>HTML5</h4>

<h2>2. HTML Elements</h2>
<h3>Block Elements</h3>
<h3>Inline Elements</h3>

<h2>3. Advanced Topics</h2>
<h3>Semantic HTML</h3>
<h4>Header Elements</h4>
<h4>Footer Elements</h4>`} />
                </div>

                <div className="code-example error">
                    <div className="code-header">
                        <span className="incorrect"> Incorrect Hierarchy</span>
                    </div>
                    <CodeBlock language="html" code={`<h1>Web Development</h1>

<h4>Introduction</h4>  <!-- Skips H2 and H3! --&gt;

<h2>HTML Basics</h2>
<h5>What is HTML?</h5>  <!-- Skips H3 and H4! --&gt;

<h3>CSS Styling</h3>  <!-- Should be H2, not H3 --&gt;`} />
                </div>

                <div className="visualization">
                    <h3>Document Outline Visualization:</h3>
                    <CodeBlock language="text" code={`H1: Complete Web Development Course

 H2: Introduction to HTML
    H3: What is HTML?
    H3: HTML History
       H4: HTML 1.0
       H4: HTML 2.0
       H4: HTML5

 H2: HTML Elements
    H3: Block Elements
    H3: Inline Elements

 H2: Advanced Topics
     H3: Semantic HTML
         H4: Header Elements
         H4: Footer Elements`} />
                </div>
            </div>

            <div className="content-section">
                <h2>SEO Benefits of Proper Headings</h2>
                <p>
                    Search engines use headings to understand your content structure and relevance.
                </p>

                <div className="seo-benefits">
                    <h3>How Headings Impact SEO:</h3>
                    <ul>
                        <li>
                            <strong>Content Hierarchy:</strong> Google uses headings to understand what your page is about.
                            H1 carries the most weight, followed by H2, H3, etc.
                        </li>
                        <li>
                            <strong>Keyword Placement:</strong> Including relevant keywords in headings (especially H1 and H2)
                            helps search engines rank your content for those terms.
                        </li>
                        <li>
                            <strong>Featured Snippets:</strong> Well-structured headings increase chances of appearing in
                            Google's featured snippets and "People also ask" sections.
                        </li>
                        <li>
                            <strong>User Experience:</strong> Clear headings improve dwell time (how long users stay),
                            which is a ranking signal.
                        </li>
                    </ul>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>SEO-Optimized Heading Structure</span>
                    </div>
                    <CodeBlock language="html" code={`<h1>How to Learn HTML for Beginners (Primary Keyword)</h1>

<h2>What is HTML? (Related Question)</h2>
<p>Content explaining HTML basics...</p>

<h2>Why Learn HTML in 2025? (Value Proposition)</h2>
<p>Content explaining benefits...</p>

<h2>HTML Learning Roadmap for Beginners (Secondary Keyword)</h2>
<h3>Step 1: Understand HTML Syntax</h3>
<p>Content about syntax...</p>

<h3>Step 2: Learn Essential HTML Tags</h3>
<p>Content about tags...</p>

<h2>Best HTML Learning Resources (Related Topic)</h2>
<h3>Free HTML Tutorials</h3>
<h3>Paid HTML Courses</h3>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Accessibility: Headings for Screen Readers</h2>
                <p>
                    Screen reader users often navigate pages by jumping between headings. Proper hierarchy is crucial for accessibility.
                </p>

                <div className="accessibility-tips">
                    <h3>Accessibility Best Practices:</h3>
                    <ul>
                        <li>
                            <strong>Navigation Aid:</strong> Screen readers can list all headings on a page, allowing users
                            to quickly jump to sections.
                        </li>
                        <li>
                            <strong>Descriptive Text:</strong> Make headings self-explanatory. "Our Services" is better than "Services".
                        </li>
                        <li>
                            <strong>Avoid "Click Here":</strong> Headings should describe content, not actions.
                        </li>
                        <li>
                            <strong>Don't use headings for styling:</strong> If you need bigger text, use CSS, not H tags.
                        </li>
                    </ul>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Accessible vs. Inaccessible Headings</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- Accessible --&gt;
<h2>Our Premium Web Development Services</h2>
<h3>Front-End Development with React</h3>
<h3>Back-End Development with Node.js</h3>

<!--  Not Accessible --&gt;
<h2>Services</h2>  <!-- Too vague --&gt;
<h3>Click Here</h3>  <!-- Not descriptive --&gt;
<h3>Learn More</h3>  <!-- Doesn't explain what you'll learn --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Styling Headings with CSS</h2>
                <p>
                    Never choose a heading level based on its default size. Use CSS to style headings while maintaining semantic structure.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>CSS Heading Styling</span>
                    </div>
                    <CodeBlock language="css" code={`/* Reset default heading sizes */
h1, h2, h3, h4, h5, h6 {
    margin: 0;
    font-weight: bold;
    line-height: 1.2;
}

/* Set custom sizes */
h1 {
    font-size: 2.5rem;  /* 40px if base is 16px */
    margin-bottom: 1.5rem;
    color: #333;
}

h2 {
    font-size: 2rem;  /* 32px */
    margin-top: 2rem;
    margin-bottom: 1rem;
    color: #444;
}

h3 {
    font-size: 1.75rem;  /* 28px */
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
    color: #555;
}

/* Make an H3 look like an H1 if needed */
h3.hero-heading {
    font-size: 2.5rem;
    color: #333;
}

/* Responsive typography */
@media (max-width: 768px) {
    h1 { font-size: 2rem; }
    h2 { font-size: 1.75rem; }
    h3 { font-size: 1.5rem; }
}`} />
                </div>

                <div className="tip-box">
                    <strong> Pro Tip:</strong> Use relative units (rem or em) instead of pixels. This respects
                    user's browser settings and improves accessibility.
                </div>
            </div>

            <div className="content-section">
                <h2>Common Heading Mistakes</h2>

                <div className="mistakes-grid">
                    <div className="mistake-card">
                        <h3> Mistake #1: Multiple H1s</h3>
                        <p><strong>Problem:</strong> Using multiple H1 elements confuses search engines about your page's main topic.</p>
                        <div className="code-example error">
                            <CodeBlock language="html" code={`<h1>Welcome to Our Site</h1>
<h1>About Our Services</h1>  <!-- Wrong! --&gt;
<h1>Contact Us</h1>  <!-- Wrong! --&gt;`} />
                        </div>
                        <p><strong>Solution:</strong> Use one H1, then H2 for major sections:</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<h1>Welcome to Our Site</h1>
<h2>About Our Services</h2>
<h2>Contact Us</h2>`} />
                        </div>
                    </div>

                    <div className="mistake-card">
                        <h3> Mistake #2: Skipping Levels</h3>
                        <p><strong>Problem:</strong> Jumping from H2 to H4 breaks the logical outline.</p>
                        <div className="code-example error">
                            <CodeBlock language="html" code={`<h2>Main Section</h2>
<h4>Subsection</h4>  <!-- Wrong! Skips H3 --&gt;`} />
                        </div>
                        <p><strong>Solution:</strong> Follow sequential order:</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<h2>Main Section</h2>
<h3>Subsection</h3>
<h4>Sub-subsection</h4>`} />
                        </div>
                    </div>

                    <div className="mistake-card">
                        <h3> Mistake #3: Using Headings for Styling</h3>
                        <p><strong>Problem:</strong> Choosing heading levels based on size, not structure.</p>
                        <div className="code-example error">
                            <CodeBlock language="html" code={`<h5>This is important text</h5>  <!-- Wrong! H5 for big text --&gt;`} />
                        </div>
                        <p><strong>Solution:</strong> Use appropriate heading level, then style with CSS:</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<h2 class="large-text">This is important text</h2>

<style>
.large-text {
    font-size: 2.5rem;
}
</style>`} />
                        </div>
                    </div>

                    <div className="mistake-card">
                        <h3> Mistake #4: Empty or Meaningless Headings</h3>
                        <p><strong>Problem:</strong> Headings that don't describe content.</p>
                        <div className="code-example error">
                            <CodeBlock language="html" code={`<h2>Section</h2>  <!-- Too vague --&gt;
<h2>Click Here</h2>  <!-- Not descriptive --&gt;
<h2>More Info</h2>  <!-- What info? --&gt;`} />
                        </div>
                        <p><strong>Solution:</strong> Be specific and descriptive:</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<h2>Our Web Development Services</h2>
<h2>How to Get Started</h2>
<h2>Pricing and Plans</h2>`} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Headings Checklist</h2>
                <div className="checklist">
                    <h3>Before Publishing, Verify:</h3>
                    <ul>
                        <li> Page has exactly one H1</li>
                        <li> H1 describes the main topic</li>
                        <li> Headings follow sequential order (no skipped levels)</li>
                        <li> Headings create a logical outline</li>
                        <li> Headings are descriptive and meaningful</li>
                        <li> Keywords included naturally in headings</li>
                        <li> Headings work for both sighted users and screen reader users</li>
                        <li> Styling is done with CSS, not heading choice</li>
                    </ul>
                </div>
            </div>

        </div>
    );
};

export default HTMLHeadings;




